import { colors, spacing } from '@akin-home/theme';
import React, { useCallback, useRef } from 'react';
import { StyleSheet, Text, View, ViewStyle } from 'react-native';
import { OneDimensionJoystick } from './OneDimensionJoystick';
import { ThrottledButton } from './ThrottledButton';

type GrabberState = {
  base: number;
  arm: number;
  sweep: number;
};

interface GrabberTeleopPanelProps {
  onChange: (data: GrabberState) => void;
  throttle?: number;
  stickyRate?: number;
  style?: ViewStyle;
  disabled?: boolean;
}

const TIME_TO_MAX = 1500;
export const GrabberTeleopPanel = (props: GrabberTeleopPanelProps) => {
  const latestVal = useRef<GrabberState>({
    base: 0,
    arm: 0,
    sweep: 0,
  });

  const handleOnChange = useCallback(
    (data: Partial<GrabberState>) => {
      latestVal.current = { ...latestVal.current, ...data };
      props.onChange(latestVal.current);
    },
    [props]
  );

  return (
    <View style={[styles.container, props.style]}>
      <View style={styles.innerBodyContainer}>
        <Text
          style={{
            fontSize: 18,
            alignSelf: 'center',
          }}
        >
          Base
        </Text>
        <OneDimensionJoystick
          onChange={(value) => {
            handleOnChange({ base: value });
          }}
          length={100}
          breadth={50}
          throttle={props.throttle}
          stickyRate={props.stickyRate}
          disabled={props.disabled}
        />
      </View>

      <View style={styles.innerBodyContainer}>
        <Text
          style={{
            fontSize: 18,
            alignSelf: 'center',
          }}
        >
          Arm
        </Text>
        <OneDimensionJoystick
          onChange={(value) => {
            handleOnChange({ arm: value });
          }}
          length={100}
          breadth={50}
          throttle={props.throttle}
          stickyRate={props.stickyRate}
          disabled={props.disabled}
        />
      </View>

      <View style={[styles.innerBodyContainer]}>
        <Text
          style={{
            fontSize: 18,
          }}
        >
          Sweep
        </Text>

        <View style={styles.buttonContainer}>
          <ThrottledButton
            onChange={(pressed, timeSincePressed) => {
              handleOnChange({
                sweep: pressed
                  ? Math.min(Math.max(timeSincePressed / TIME_TO_MAX, 0), 1)
                  : 0,
              });
            }}
            size={'large'}
            icon="arrow-rotate-right"
            iconSize={35}
            iconColor={props.disabled ? colors.grey[300] : 'white'}
            style={props.disabled ? styles.disabledButton : styles.button}
            throttle={props.throttle}
            stickyRate={props.stickyRate}
            disabled={props.disabled}
          />
          <ThrottledButton
            onChange={(pressed, timeSincePressed) => {
              handleOnChange({
                sweep: pressed
                  ? -Math.min(Math.max(timeSincePressed / TIME_TO_MAX, 0), 1)
                  : 0,
              });
            }}
            size={'large'}
            icon="arrow-rotate-left"
            iconSize={35}
            iconColor={props.disabled ? colors.grey[300] : 'white'}
            style={props.disabled ? styles.disabledButton : styles.button}
            throttle={props.throttle}
            stickyRate={props.stickyRate}
            disabled={props.disabled}
          />
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    justifyContent: 'center',
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing[2],
    borderRadius: 10,
  },
  innerBodyContainer: {
    marginHorizontal: spacing[3],
  },
  buttonContainer: {
    flexDirection: 'row',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'black',
    padding: spacing[1],
  },
  button: {
    backgroundColor: colors.secondary[700],
    margin: spacing[1],
  },
  disabledButton: {
    backgroundColor: colors.grey.dim,
    margin: spacing[1],
  },
});
